import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MarketplaceLayout } from "../components/MarketplaceLayout";
import { useI18n } from "../contexts/I18nContext";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import api, { fileUrl, formatApiError } from "../lib/api";
import { FramedArtwork } from "../components/FramedArtwork";
import { FramingOptionSelector } from "../components/FramingOptionSelector";
import { toast } from "sonner";
import { Sparkles, Layers } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

function ExclusiveBuyoutModal({ artwork, material, onClose, onSuccess }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    shipping_name: user?.name || "",
    shipping_email: user?.email || "",
    shipping_phone: "",
    shipping_address: "",
    shipping_city: "",
    shipping_country: "España",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/artworks/${artwork.id}/exclusive-buyout`, {
        ...form,
        material: material?.code,
      });
      toast.success("¡Compra en exclusiva confirmada!");
      onSuccess(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const inputCls = "w-full border border-neutral-300 px-3 py-2.5 outline-none focus:border-black bg-white text-sm";
  const remaining = artwork.prints_remaining ?? "—";
  const totalEdition = artwork.max_impressions ?? "Abierta";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="buyout-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light">Comprar «{artwork.title}» en exclusiva</DialogTitle>
          <DialogDescription>
            Adquieres la edición completa restante y la obra se retira del catálogo público. Tuya, y solo tuya.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-neutral-50 border-l-2 border-black p-4 my-3">
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <div className="overline text-neutral-500">Edición original</div>
              <div className="font-serif text-xl mt-1">{totalEdition}</div>
            </div>
            <div>
              <div className="overline text-neutral-500">Impresiones que adquieres</div>
              <div className="font-serif text-xl mt-1">{remaining}</div>
            </div>
            <div className="col-span-2 pt-3 border-t border-neutral-200">
              <div className="overline text-neutral-500">Precio total (incluye premium de exclusividad)</div>
              <div className="font-serif text-3xl mt-1">€{artwork.exclusive_buyout_price?.toFixed(2)}</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Nombre completo" value={form.shipping_name} onChange={(e) => setForm({ ...form, shipping_name: e.target.value })} className={inputCls} data-testid="buyout-name" />
          <div className="grid grid-cols-2 gap-3">
            <input required type="email" placeholder="Email" value={form.shipping_email} onChange={(e) => setForm({ ...form, shipping_email: e.target.value })} className={inputCls} data-testid="buyout-email" />
            <input required placeholder="Teléfono" value={form.shipping_phone} onChange={(e) => setForm({ ...form, shipping_phone: e.target.value })} className={inputCls} data-testid="buyout-phone" />
          </div>
          <input required placeholder="Dirección de envío" value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} className={inputCls} data-testid="buyout-address" />
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Ciudad" value={form.shipping_city} onChange={(e) => setForm({ ...form, shipping_city: e.target.value })} className={inputCls} data-testid="buyout-city" />
            <input required placeholder="País" value={form.shipping_country} onChange={(e) => setForm({ ...form, shipping_country: e.target.value })} className={inputCls} data-testid="buyout-country" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="overline text-xs px-4 py-2.5 border border-neutral-300 hover:bg-neutral-50" data-testid="buyout-cancel">Cancelar</button>
            <button disabled={loading} type="submit" className="overline text-xs px-6 py-2.5 bg-black text-white hover:bg-neutral-800 disabled:opacity-50" data-testid="buyout-submit">
              {loading ? "Procesando..." : `Adquirir en exclusiva · €${artwork.exclusive_buyout_price?.toFixed(2)}`}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ArtworkDetailPage() {
  const { id } = useParams();
  const { t } = useI18n();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedFraming, setSelectedFraming] = useState(null);
  const [showBuyout, setShowBuyout] = useState(false);
  const [mockups, setMockups] = useState([]);

  const loadArtwork = () => api.get(`/artworks/${id}`).then((r) => {
    setA(r.data);
    if (r.data?.sizes?.length) setSelectedSize((cur) => cur || r.data.sizes[0]);
  }).catch(() => setA(null));

  useEffect(() => {
    loadArtwork();
    api.get("/materials").then((r) => {
      setMaterials(r.data);
      const def = r.data.find((m) => m.code === "paper_etching_310") || r.data[0];
      setSelectedMaterial(def);
    }).catch(() => {});
    api.get("/mockup-templates").then((r) => {
      console.log("Mockups loaded:", r.data);
      setMockups(r.data || []);
    }).catch((e) => console.warn("Mockups load failed:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!a) return <MarketplaceLayout><div className="max-w-7xl mx-auto px-6 py-32 text-center overline text-neutral-400">Loading...</div></MarketplaceLayout>;

  const hasSizes = a.sizes && a.sizes.length > 0;
  const displayPrice = selectedFraming ? selectedFraming.price : (selectedSize ? selectedSize.price : a.price);

  const add = () => {
    addItem(a, selectedSize, selectedMaterial, selectedFraming);
    toast.success(`${a.title}${selectedSize ? ` (${selectedSize.label})` : ""} → ${t.cart.title}`);
  };
  const buy = () => {
    addItem(a, selectedSize, selectedMaterial, selectedFraming);
    navigate("/cart");
  };

  return (
    <MarketplaceLayout>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-20 grid lg:grid-cols-12 gap-12 lg:gap-20" data-testid="artwork-detail">
        <div className="lg:col-span-7">
          <FramedArtwork
            src={fileUrl(a.image_path, "lg")}
            alt={a.title}
            ratio={a.image_width && a.image_height ? a.image_width / a.image_height : undefined}
            aspectClass="aspect-[4/5]"
            hover={false}
            imgTestId="artwork-image"
          />
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-24 self-start">
          <div className="overline text-neutral-500 mb-3">{a.category}</div>
          <h1 className="font-serif text-4xl lg:text-5xl font-light leading-tight" data-testid="artwork-title">{a.title}</h1>
          <div className="mt-3 text-neutral-500 text-sm">
            {t.artwork.by}{" "}
            <Link to={`/artists/${a.artist_id}`} className="text-neutral-900 hover:underline" data-testid="artist-link">
              {a.artist_name}
            </Link>
          </div>

          {(a.is_limited_edition || !a.is_limited_edition) && (
            <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-wider border border-neutral-900 px-3 py-1.5" data-testid="edition-badge">
              <Layers size={12} strokeWidth={1.5} />
              {a.is_limited_edition
                ? <span>Edición limitada · <strong>{a.prints_remaining}</strong> de {a.max_impressions} disponibles</span>
                : <span>Edición abierta</span>}
            </div>
          )}

          <div className="mt-8 text-3xl font-serif" data-testid="artwork-price">€{displayPrice.toFixed(2)}</div>

          {hasSizes && (
            <div className="mt-8">
              <div className="overline text-neutral-500 mb-3">Tamaño · Print fine art</div>
              <div className="space-y-2" data-testid="size-selector">
                {a.sizes.map((s) => {
                  const isSel = selectedSize?.label === s.label;
                  return (
                    <button
                      key={s.label}
                      onClick={() => setSelectedSize(s)}
                      className={`w-full flex items-center justify-between border px-4 py-3 transition-colors ${isSel ? "border-black bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`}
                      data-testid={`size-option-${s.label}`}
                    >
                      <div className="text-left">
                        <div className="text-sm font-medium">{s.label}</div>
                        <div className="text-xs text-neutral-500">{s.width_cm} × {s.height_cm} cm</div>
                      </div>
                      <div className="font-serif text-lg">€{s.price.toFixed(2)}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {materials.length > 0 && (
            <div className="mt-8">
              <div className="overline text-neutral-500 mb-3">Material · Soporte de impresión</div>
              <div className="space-y-2" data-testid="material-selector">
                {materials.map((m) => {
                  const isSel = selectedMaterial?.code === m.code;
                  return (
                    <button
                      key={m.code}
                      onClick={() => setSelectedMaterial(m)}
                      className={`w-full text-left border px-4 py-3 transition-colors ${isSel ? "border-black bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`}
                      data-testid={`material-option-${m.code}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium">{m.name}</div>
                          <div className="text-xs text-neutral-500 mt-0.5">{m.description}</div>
                        </div>
                        <span className="overline text-[10px] text-neutral-500 whitespace-nowrap pt-1">{m.ideal_for}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-neutral-500 mt-3 leading-relaxed">
                Todas nuestras obras se imprimen sobre soportes de calidad museo. Mismo precio independientemente del material.
              </p>
            </div>
          )}

          <FramingOptionSelector
            options={a.standard_framing || []}
            selected={selectedFraming}
            onSelect={setSelectedFraming}
          />

          <div className="mt-10 space-y-3">
            <button onClick={buy} className="w-full bg-black text-white py-4 overline hover:bg-neutral-800 transition-colors" data-testid="buy-now-btn">
              {t.artwork.buyNow}
            </button>
            <button onClick={add} className="w-full border border-black py-4 overline hover:bg-neutral-100 transition-colors" data-testid="add-to-cart-btn">
              {t.artwork.addToCart}
            </button>
            {a.exclusive_buyout_price && a.exclusive_buyout_price > 0 && (
              <button onClick={() => setShowBuyout(true)} className="w-full border border-neutral-900 bg-neutral-50 py-4 overline hover:bg-neutral-100 transition-colors flex items-center justify-center gap-3" data-testid="buyout-btn">
                <Sparkles size={14} strokeWidth={1.5} />
                Comprar en exclusiva · €{a.exclusive_buyout_price.toFixed(2)}
              </button>
            )}
            {a.exclusive_buyout_price && a.exclusive_buyout_price > 0 && (
              <p className="text-[11px] text-neutral-500 leading-relaxed pt-1">
                Adquieres la edición completa restante. Tras la compra, la obra se retira del catálogo y nadie más puede comprarla.
              </p>
            )}
          </div>

          <div className="mt-12 space-y-8">
            <div>
              <div className="overline text-neutral-500 mb-3">{t.artwork.description}</div>
              <p className="text-sm leading-relaxed text-neutral-700" data-testid="artwork-description">{a.description}</p>
            </div>
            <div>
              <div className="overline text-neutral-500 mb-3">{t.artwork.shipping}</div>
              <p className="text-sm leading-relaxed text-neutral-700">{t.artwork.shippingText}</p>
            </div>
          </div>
        </div>
      </section>

      {(() => {
        const visibleMockups = mockups.filter((m) => !m.categories || m.categories.length === 0 || m.categories.includes(a.category));
        if (visibleMockups.length === 0) return null;
        return (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 lg:pb-20" data-testid="mockups-section">
          <div className="border-t border-neutral-200 pt-12 lg:pt-20">
            <div className="overline text-neutral-500 mb-2">Visualizador</div>
            <h2 className="font-serif text-3xl lg:text-4xl font-light tracking-tight mb-3">La obra en distintos espacios</h2>
            <p className="text-sm text-neutral-500 max-w-2xl mb-10">Previsualiza cómo se verá la obra montada en distintos contextos reales.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {visibleMockups.map((m) => (
                <div key={m.id} className="relative bg-neutral-100 overflow-hidden aspect-[4/3]" data-testid={`mockup-render-${m.id}`}>
                  <img src={fileUrl(m.image_path)} alt={m.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  <div
                    className="absolute overflow-hidden shadow-2xl"
                    style={{
                      left: `${m.hole_x * 100}%`,
                      top: `${m.hole_y * 100}%`,
                      width: `${m.hole_w * 100}%`,
                      height: `${m.hole_h * 100}%`,
                    }}
                  >
                    <img src={fileUrl(a.image_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="absolute bottom-2 left-2 overline text-[10px] bg-black/70 text-white px-2 py-1">{m.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
        );
      })()}

      {a.context_images && a.context_images.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 lg:pb-32" data-testid="context-images-section">
          <div className="border-t border-neutral-200 pt-12 lg:pt-20">
            <div className="overline text-neutral-500 mb-2">En contexto</div>
            <h2 className="font-serif text-3xl lg:text-4xl font-light tracking-tight mb-10">Cómo se ve en una pared real</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {a.context_images.map((path, idx) => (
                <div key={idx} className="aspect-[4/3] bg-neutral-100 overflow-hidden" data-testid={`context-image-${idx}`}>
                  <img
                    src={fileUrl(path)}
                    alt={`${a.title} — contexto ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {showBuyout && (
        <ExclusiveBuyoutModal
          artwork={a}
          material={selectedMaterial}
          onClose={() => setShowBuyout(false)}
          onSuccess={() => { setShowBuyout(false); loadArtwork(); navigate("/"); }}
        />
      )}
    </MarketplaceLayout>
  );
}
